import { useState } from "react";
import { motion } from "framer-motion";
import Reveal from "./Reveal";

const VALUES = [
  { id: "partner", title: "A partner, not an employee.", desc: "Someone who thinks with you, not just takes orders." },
  { id: "privacy", title: "Privacy.", desc: "We keep client work private. That's why we don't publish testimonials or results without permission." },
  { id: "roi", title: "ROI.", desc: "You care about what the content brings back, not just how good it looks." },
  { id: "time", title: "Time over money.", desc: "You know your time is worth more than doing everything yourself." },
  { id: "success", title: "Your success over a quick cash grab.", desc: "You want someone who genuinely cares about growing with you, not someone looking to squeeze another payment out of you." },
  { id: "quality", title: "Quality over shortcuts.", desc: "You'd rather do it right than just get content out." },
  { id: "consistency", title: "Consistency.", desc: "You want a system that keeps producing, not random bursts of content." },
  { id: "data", title: "Data over guesswork.", desc: "You're willing to test, learn, and improve based on what actually works." },
  { id: "long-term", title: "Long-term growth.", desc: "You're building an audience and brand, not chasing one viral video." },
  { id: "professional", title: "Being easy to work with and professional.", desc: "You communicate clearly, trust the process, and let experts do their job." },
  { id: "ownership", title: "Ownership.", desc: "You want someone who sees problems and fixes them without waiting to be told." },
];

const ValueCard = ({ v, i }) => {
  const [burst, setBurst] = useState(0);
  return (
    <div
      onMouseEnter={() => setBurst((b) => b + 1)}
      className="group relative h-full rounded-[28px] border border-black/[0.06] bg-white p-8 shadow-[0_14px_40px_rgba(0,0,0,0.06)] transition-all duration-500 ease-out hover:z-20 hover:scale-[1.07] hover:-translate-y-2 hover:shadow-[0_44px_90px_rgba(0,0,0,0.18)] cursor-default flex flex-col"
      data-testid={`value-row-${v.id}`}
    >
      {burst > 0 && (
        <span key={burst} className="pointer-events-none absolute inset-0 z-30" aria-hidden="true">
          {Array.from({ length: 9 }).map((_, p) => {
            const angle = (p / 9) * Math.PI * 2 + burst;
            const dist = 64 + ((p * 37) % 52);
            return (
              <motion.span
                key={`${burst}-${p}`}
                initial={{ x: 0, y: 0, opacity: 1, scale: 0.4, rotate: 0 }}
                animate={{
                  x: Math.cos(angle) * dist,
                  y: Math.sin(angle) * dist,
                  opacity: 0,
                  scale: 1.15,
                  rotate: (p % 2 ? 1 : -1) * 160,
                }}
                transition={{ duration: 0.85, ease: "easeOut" }}
                className="absolute font-display font-bold text-[#1d1d1f] text-sm"
                style={{ left: "50%", top: "42%" }}
              >
                V
              </motion.span>
            );
          })}
        </span>
      )}
      <span className="text-xs font-medium text-[#aeaeb2] transition-colors duration-300 group-hover:text-[#1d1d1f]">
        {String(i + 1).padStart(2, "0")}
      </span>
      <h3 className="mt-6 font-display text-xl sm:text-2xl font-semibold tracking-[-0.02em] text-[#1d1d1f]">
        {v.title}
      </h3>
      <p className="mt-3 text-[15px] text-[#6e6e73] leading-relaxed">{v.desc}</p>
    </div>
  );
};

export default function Values() {
  return (
    <section className="py-28 sm:py-40 bg-white" data-testid="values-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
          <span className="eyebrow text-[#86868b]">06</span>
          <h2 className="mt-5 font-fancy text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] text-[#1d1d1f]">
            We're the best fit for you if you value:
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {VALUES.map((v, i) => (
            <Reveal key={v.id} delay={Math.min(i * 0.04, 0.3)} y={24} className="h-full">
              <ValueCard v={v} i={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
